import { repairLine } from "./rule-catalog";
import { ASSET_CHOICE_MIN_ASSETS } from "./question-capabilities";
import { countWithinBounds } from "./rule-evaluators";

type JsonObject = Record<string, unknown>;

export const ASSET_COMPARISON_VALIDATION_CODES = [
  "asset_comparison_participants_too_few",
  "asset_comparison_participant_duplicate",
  "asset_comparison_choice_ref_required",
  "asset_comparison_choice_ref_count_invalid",
  "asset_comparison_choice_ref_out_of_scope",
  "asset_comparison_choice_duplicate",
  "asset_comparison_participant_choice_missing",
  "asset_comparison_exclusive_invalid",
  "asset_comparison_follow_up_parent_invalid",
  "asset_comparison_follow_up_scope_invalid",
  "asset_roster_catalog_mismatch",
] as const;

export type AssetComparisonValidationCode =
  (typeof ASSET_COMPARISON_VALIDATION_CODES)[number];

export interface AssetComparisonValidationIssue {
  path: string;
  code: AssetComparisonValidationCode;
  message: string;
  severity: "error";
}

function isRecord(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asRecords(value: unknown): Array<JsonObject> {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function issue(
  path: string,
  code: AssetComparisonValidationCode,
  message: string,
): AssetComparisonValidationIssue {
  return { path, code, message, severity: "error" };
}

function refsWithRole(record: JsonObject, role: string): Array<JsonObject> {
  return asRecords(record.assetRefs).filter((ref) => ref.role === role);
}

function refAssetId(ref: JsonObject): string | null {
  return typeof ref.assetId === "string" && ref.assetId.length > 0
    ? ref.assetId
    : null;
}

function responseChoices(response: JsonObject): {
  key: "options" | "categories";
  values: Array<JsonObject>;
} | null {
  if (Array.isArray(response.options)) {
    return { key: "options", values: asRecords(response.options) };
  }
  if (Array.isArray(response.categories)) {
    return { key: "categories", values: asRecords(response.categories) };
  }
  return null;
}

function comparisonParticipantIds(question: JsonObject): Array<string> {
  return refsWithRole(question, "comparison")
    .map(refAssetId)
    .filter((assetId): assetId is string => assetId !== null);
}

// The fewest participants a comparison needs is the fewest tested assets an
// asset choice offers (`assetChoiceMinAssets`, question-capability registry).
const COMPARISON_PARTICIPANT_FACTS = { min: ASSET_CHOICE_MIN_ASSETS };

/**
 * The typed roster test (plan 09 S3.3): the response's non-exclusive choices
 * (a grid's categories) are exactly the catalog of `assetType`, one choice per
 * asset, each with one typed `choice` reference by asset id. Exclusive answers
 * stand beside it and are not counted. Reads ids and roles only, never text.
 * V10 holds a marked roster to it; the S3.5 backfill marks a stored roster
 * that passes it.
 */
export function isExactAssetRoster(
  response: unknown,
  catalog: ReadonlyArray<{ id?: unknown; type?: unknown }>,
  assetType: unknown,
): boolean {
  if (!isRecord(response)) {
    return false;
  }
  const expected = new Set(
    catalog
      .filter((asset) => asset.type === assetType)
      .map((asset) => asset.id),
  );
  const offered = (responseChoices(response)?.values ?? [])
    .filter((choice) => choice.exclusive !== true)
    .map((choice) => refsWithRole(choice, "choice").map(refAssetId));
  const offeredIds = new Set(offered.map((ids) => ids[0]));
  return (
    offered.every((ids) => ids.length === 1 && ids[0] !== null) &&
    offeredIds.size === offered.length &&
    offeredIds.size === expected.size &&
    [...offeredIds].every((assetId) => expected.has(assetId))
  );
}

/**
 * A response whose `optionSource` names a family is an exact roster of it
 * (`isExactAssetRoster`). The operations applier keeps it so as assets come
 * and go. A brand-roster list (C13-1) follows the brand facts, not the
 * catalog.
 */
function optionSourceIssues(
  question: JsonObject,
  questionPath: string,
  catalog: Array<JsonObject>,
): Array<AssetComparisonValidationIssue> {
  const response = isRecord(question.response) ? question.response : {};
  if (
    !isRecord(response.optionSource) ||
    response.optionSource.kind !== "tested_assets"
  ) {
    return [];
  }
  const assetType = response.optionSource.assetType;
  return isExactAssetRoster(response, catalog, assetType)
    ? []
    : [
        issue(
          `${questionPath}.response.optionSource`,
          "asset_roster_catalog_mismatch",
          repairLine("asset_roster_catalog_mismatch"),
        ),
      ];
}

/** A non-exclusive choice with a typed `choice` reference: it stands for a tested asset. */
export function isAssetBackedChoice(choice: unknown): boolean {
  return (
    isRecord(choice) &&
    choice.exclusive !== true &&
    refsWithRole(choice, "choice").length > 0
  );
}

/**
 * A comparison (typed `comparison` refs) that answers with an option list
 * (select or ranking) answers with its participants: each non-exclusive
 * option must carry one typed `choice` reference. An option added by hand can
 * therefore only be an exclusive escape (`asset_comparison_choice_ref_required`).
 */
export function answersWithComparisonParticipants(question: unknown): boolean {
  if (
    !isRecord(question) ||
    refsWithRole(question, "comparison").length === 0
  ) {
    return false;
  }
  const response = isRecord(question.response) ? question.response : {};
  return responseChoices(response)?.key === "options";
}

/**
 * Whether removing the option at `index` leaves the comparison with fewer than
 * two participants (`asset_comparison_participants_too_few`). The participants
 * are the assets the options stand for (the operations applier keeps them in
 * step with the options), so only removing one of the last two is refused.
 */
export function removingOptionLeavesTooFewParticipants(
  question: unknown,
  index: number,
): boolean {
  if (!answersWithComparisonParticipants(question)) {
    return false;
  }
  const participants = new Set(
    comparisonParticipantIds(question as JsonObject),
  );
  const options = asRecords(
    (question as { response: JsonObject }).response.options,
  );
  const participantOptions = options.filter(
    (option) =>
      isAssetBackedChoice(option) &&
      refsWithRole(option, "choice").some((ref) =>
        participants.has(refAssetId(ref) ?? ""),
      ),
  );
  return (
    participantOptions.includes(options[index]) &&
    countWithinBounds(COMPARISON_PARTICIPANT_FACTS, {
      count: participantOptions.length - 1,
    }).length > 0
  );
}

/**
 * Validates only explicit canonical identities. It never reads question text,
 * option labels, tags, asset names, or asset family when deciding whether a
 * comparison is coherent.
 *
 * A comparison that answers with an option list (select or ranking) answers
 * with its participants: every participant/choice mapping must be bijective,
 * so an option list without typed `role="choice"` refs is refused. A grid's
 * categories are a scale applied per row, so a comparison grid is held to the
 * bijection only once a non-exclusive category carries a typed choice ref, and
 * a comparison with no choice list at all (a rating, an open end) is a
 * collective question whose answer surface is left alone.
 */
export function assetComparisonCoherenceIssues(
  payload: unknown,
): Array<AssetComparisonValidationIssue> {
  if (!isRecord(payload)) {
    return [];
  }

  const testedAssets = isRecord(payload.testedAssets)
    ? payload.testedAssets
    : {};
  const catalog = asRecords(testedAssets.assets);
  const catalogIds = new Set(
    catalog
      .map((asset) =>
        typeof asset.id === "string" && asset.id.length > 0 ? asset.id : null,
      )
      .filter((assetId): assetId is string => assetId !== null),
  );
  const questions = asRecords(payload.researchQuestions);
  const earlierQuestionsById = new Map<string, JsonObject>();
  const issues: Array<AssetComparisonValidationIssue> = [];

  questions.forEach((question, questionIndex) => {
    const questionPath = `researchQuestions[${questionIndex}]`;
    const comparisonRefs = refsWithRole(question, "comparison");
    const participantIds = comparisonParticipantIds(question);
    const participantSet = new Set(participantIds);

    if (comparisonRefs.length > 0) {
      const duplicateIds = new Set<string>();
      const seenIds = new Set<string>();
      participantIds.forEach((assetId) => {
        if (seenIds.has(assetId)) {
          duplicateIds.add(assetId);
        }
        seenIds.add(assetId);
      });
      duplicateIds.forEach((assetId) => {
        issues.push(
          issue(
            `${questionPath}.assetRefs`,
            "asset_comparison_participant_duplicate",
            repairLine("asset_comparison_participant_duplicate"),
          ),
        );
      });

      const catalogBackedParticipants = new Set(
        participantIds.filter((assetId) => catalogIds.has(assetId)),
      );
      const [tooFewParticipants] = countWithinBounds(
        COMPARISON_PARTICIPANT_FACTS,
        { count: catalogBackedParticipants.size },
      );
      if (tooFewParticipants?.reason === "below_min") {
        issues.push(
          issue(
            `${questionPath}.assetRefs`,
            "asset_comparison_participants_too_few",
            repairLine("asset_comparison_participants_too_few"),
          ),
        );
      }

      const response = isRecord(question.response) ? question.response : {};
      const choiceSurface = responseChoices(response);
      const isAssetBackedChoiceSurface =
        choiceSurface?.values.some(isAssetBackedChoice);

      if (
        choiceSurface &&
        (answersWithComparisonParticipants(question) ||
          isAssetBackedChoiceSurface)
      ) {
        const mappedParticipantCounts = new Map<string, number>();
        choiceSurface.values.forEach((choice, choiceIndex) => {
          const choicePath = `${questionPath}.response.${choiceSurface.key}[${choiceIndex}]`;
          const allAssetRefs = asRecords(choice.assetRefs);
          if (choice.exclusive === true) {
            if (allAssetRefs.length > 0) {
              issues.push(
                issue(
                  `${choicePath}.assetRefs`,
                  "asset_comparison_exclusive_invalid",
                  repairLine("asset_comparison_exclusive_invalid"),
                ),
              );
            }
            return;
          }

          const choiceRefs = refsWithRole(choice, "choice");
          if (choiceRefs.length === 0) {
            issues.push(
              issue(
                `${choicePath}.assetRefs`,
                "asset_comparison_choice_ref_required",
                repairLine("asset_comparison_choice_ref_required"),
              ),
            );
            return;
          }
          if (choiceRefs.length !== 1) {
            issues.push(
              issue(
                `${choicePath}.assetRefs`,
                "asset_comparison_choice_ref_count_invalid",
                repairLine("asset_comparison_choice_ref_count_invalid"),
              ),
            );
            return;
          }

          const assetId = refAssetId(choiceRefs[0]);
          if (
            assetId === null ||
            !catalogIds.has(assetId) ||
            !participantSet.has(assetId)
          ) {
            issues.push(
              issue(
                `${choicePath}.assetRefs[0]`,
                "asset_comparison_choice_ref_out_of_scope",
                repairLine("asset_comparison_choice_ref_out_of_scope"),
              ),
            );
            return;
          }
          mappedParticipantCounts.set(
            assetId,
            (mappedParticipantCounts.get(assetId) ?? 0) + 1,
          );
        });

        mappedParticipantCounts.forEach((count, assetId) => {
          if (count > 1) {
            issues.push(
              issue(
                `${questionPath}.response.${choiceSurface.key}`,
                "asset_comparison_choice_duplicate",
                repairLine("asset_comparison_choice_duplicate"),
              ),
            );
          }
        });
        participantSet.forEach((assetId) => {
          if (
            catalogIds.has(assetId) &&
            !mappedParticipantCounts.has(assetId)
          ) {
            issues.push(
              issue(
                `${questionPath}.response.${choiceSurface.key}`,
                "asset_comparison_participant_choice_missing",
                repairLine("asset_comparison_participant_choice_missing"),
              ),
            );
          }
        });
      }
    }

    const response = isRecord(question.response) ? question.response : {};
    const followUpIds = Array.isArray(response.followUpToQuestionIds)
      ? response.followUpToQuestionIds.filter(
          (value): value is string =>
            typeof value === "string" && value.length > 0,
        )
      : [];
    const resolvedParents = followUpIds
      .map((questionId) => earlierQuestionsById.get(questionId))
      .filter((parent): parent is JsonObject => parent !== undefined);
    const isComparisonFollowUp =
      comparisonRefs.length > 0 ||
      resolvedParents.some(
        (parent) => refsWithRole(parent, "comparison").length > 0,
      );
    if (followUpIds.length > 0 && isComparisonFollowUp) {
      const parent =
        followUpIds.length === 1
          ? earlierQuestionsById.get(followUpIds[0])
          : undefined;
      const parentParticipantIds = parent
        ? comparisonParticipantIds(parent)
        : [];
      const parentCatalogParticipants = new Set(
        parentParticipantIds.filter((assetId) => catalogIds.has(assetId)),
      );
      const parentIsValid =
        parent !== undefined &&
        refsWithRole(parent, "comparison").length > 0 &&
        parentCatalogParticipants.size >= 2 &&
        parentParticipantIds.length === new Set(parentParticipantIds).size &&
        parentParticipantIds.length ===
          refsWithRole(parent, "comparison").length &&
        parentParticipantIds.every((assetId) => catalogIds.has(assetId));
      if (!parentIsValid) {
        issues.push(
          issue(
            `${questionPath}.response.followUpToQuestionIds`,
            "asset_comparison_follow_up_parent_invalid",
            repairLine("asset_comparison_follow_up_parent_invalid"),
          ),
        );
      } else {
        const parentParticipantSet = new Set(parentParticipantIds);
        const inventedParticipants = Array.from(participantSet).filter(
          (assetId) => !parentParticipantSet.has(assetId),
        );
        if (inventedParticipants.length > 0) {
          issues.push(
            issue(
              `${questionPath}.assetRefs`,
              "asset_comparison_follow_up_scope_invalid",
              repairLine("asset_comparison_follow_up_scope_invalid"),
            ),
          );
        }
      }
    }

    issues.push(...optionSourceIssues(question, questionPath, catalog));

    if (typeof question.id === "string" && question.id.length > 0) {
      earlierQuestionsById.set(question.id, question);
    }
  });

  return issues;
}
